/**
 * Stable doc_code values for 各種コピー — aligned with the production admin catalog.
 * Keep in sync with backend {@code VariousCopyDocCodes}.
 * <p>
 * Career-up / drivers / skip allowlist / front+back upload follow the production mapping.
 * マイナンバー is VC012 (admin-added custom row).
 */
export const VariousCopyDocCodes = {
  RESIDENCE_CERTIFICATE: 'VC001',
  /** 建退共手帳 */
  KENTAIKYO_NOTEBOOK: 'VC002',
  /** 運転免許証 */
  DRIVERS_LICENSE: 'VC003',
  /** 通帳 */
  BANK_PASSBOOK: 'VC004',
  /** 健康診断書 */
  HEALTH_CERTIFICATE: 'VC005',
  /** 障害者手帳 */
  DISABILITY_CERTIFICATE: 'VC006',
  /** 在留カード */
  RESIDENCE_CARD: 'VC007',
  /** 資格証明書 */
  QUALIFICATION_CERTIFICATE: 'VC008',
  RESUME: 'VC009',
  /** 雇用保険関係書類 */
  EMPLOYMENT_INSURANCE: 'VC010',
  /** キャリアアップ関係書類 — disposition for FF008/FF009. */
  CAREER_UP_RELATED: 'VC011',
  /** マイナンバー — production custom row. */
  MY_NUMBER: 'VC012',
} as const;

export type VariousCopyDocCode =
  (typeof VariousCopyDocCodes)[keyof typeof VariousCopyDocCodes];

/** Docs that may show “I do not have this document” (EmpDocStatus.skipped). */
const SKIP_ALLOWED: ReadonlySet<string> = new Set([
  'VC002',
  'VC003',
  'VC006',
  'VC007',
  'VC008',
  'VC010',
  'VC011',
]);

export function isBuiltinVariousCopyDocCode(docCode: string | undefined | null): boolean {
  if (!docCode) return false;
  const m = /^VC(\d+)$/i.exec(docCode.trim());
  if (!m) return false;
  const n = Number(m[1]);
  return n >= 1 && n <= 11;
}

/**
 * Docs that may require front+back upload (card-style).
 * Production: VC003 免許, VC007 在留カード, VC011 キャリアアップ, VC012 マイナンバー.
 */
const BOTH_SIDES_REQUIRED: ReadonlySet<string> = new Set([
  VariousCopyDocCodes.DRIVERS_LICENSE,
  VariousCopyDocCodes.RESIDENCE_CARD,
  VariousCopyDocCodes.CAREER_UP_RELATED,
  VariousCopyDocCodes.MY_NUMBER,
]);

/**
 * Docs that require upload in the system, then mailing the original to head office.
 * Production: VC002 建退共手帳, VC008 資格証明書.
 */
const ORIGINAL_TO_HEAD_OFFICE: ReadonlySet<string> = new Set([
  VariousCopyDocCodes.KENTAIKYO_NOTEBOOK,
  VariousCopyDocCodes.QUALIFICATION_CERTIFICATE,
]);

export function requiresBothSidesUpload(docCode: string | undefined | null): boolean {
  if (!docCode) return false;
  return BOTH_SIDES_REQUIRED.has(docCode.trim().toUpperCase());
}

export function requiresOriginalToHeadOffice(docCode: string | undefined | null): boolean {
  if (!docCode) return false;
  return ORIGINAL_TO_HEAD_OFFICE.has(docCode.trim().toUpperCase());
}

export function allowsVariousCopySkip(docCode: string | undefined | null): boolean {
  if (!docCode) return false;
  return SKIP_ALLOWED.has(docCode.trim().toUpperCase());
}
